import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const TimeTracker = ({ technicianId }) => {
  const [isTracking, setIsTracking] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [totalBreakTime, setTotalBreakTime] = useState(0);
  const [currentLocation, setCurrentLocation] = useState(null);

  useEffect(() => {
    let interval = null;

    if (isTracking && startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTracking, startTime]);

  useEffect(() => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleClockIn = () => {
    setStartTime(Date.now());
    setIsTracking(true);
    setElapsedTime(0);
    setTotalBreakTime(0);
  };

  const handleClockOut = () => {
    setIsTracking(false);
    // Here you would save the time entry to the database
    console.log('Clock out:', {
      technicianId,
      startTime: new Date(startTime),
      endTime: new Date(),
      totalHours: (elapsedTime / 3600)?.toFixed(2),
      breakTime: (totalBreakTime / 60)?.toFixed(0),
      location: currentLocation
    });
    setStartTime(null);
    setElapsedTime(0);
  };

  const handleStartBreak = () => {
    setBreakStartTime(Date.now());
  };

  const handleEndBreak = () => {
    if (breakStartTime) {
      const breakDuration = Math.floor((Date.now() - breakStartTime) / 1000);
      setTotalBreakTime(totalBreakTime + breakDuration);
      setBreakStartTime(null);
    }
  };

  const workingTime = elapsedTime - totalBreakTime;

  return (
    <div className="space-y-6">
      {/* Time Display */}
      <div className="bg-card rounded-xl border border-border p-8 text-center">
        {!isTracking ? (
          <>
            <Icon name="Clock" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-2">Ready to Start</h3>
            <p className="text-sm text-muted-foreground mb-6">Clock in to begin tracking your work time</p>
            <Button
              variant="primary"
              size="lg"
              iconName="Play"
              iconPosition="left"
              onClick={handleClockIn}
              className="w-full max-w-xs mx-auto"
            >
              Clock In
            </Button>
          </>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-2">Total Time</p>
              <p className="text-5xl font-bold text-foreground mb-4">{formatTime(elapsedTime)}</p>
              <div className="flex items-center justify-center gap-6 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Working Time</p>
                  <p className="text-lg font-semibold text-success">{formatTime(workingTime)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Break Time</p>
                  <p className="text-lg font-semibold text-warning">{formatTime(totalBreakTime)}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-center">
              {!breakStartTime ? (
                <Button
                  variant="outline"
                  iconName="Coffee"
                  iconPosition="left"
                  onClick={handleStartBreak}
                >
                  Start Break
                </Button>
              ) : (
                <Button
                  variant="primary"
                  iconName="Play"
                  iconPosition="left"
                  onClick={handleEndBreak}
                >
                  End Break
                </Button>
              )}
              <Button
                variant="primary"
                iconName="StopCircle"
                iconPosition="left"
                onClick={handleClockOut}
              >
                Clock Out
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Session Info */}
      {isTracking && startTime && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Current Session</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Clock In Time</span>
              <span className="text-sm font-medium text-foreground">
                {format(new Date(startTime), 'h:mm a')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <span className={`text-sm font-medium ${
                breakStartTime ? 'text-warning' : 'text-success'
              }`}>
                {breakStartTime ? 'On Break' : 'Working'}
              </span>
            </div>
            {currentLocation && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="text-sm font-medium text-foreground flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  GPS Verified
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Today Summary */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Today Summary</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <Icon name="Clock" size={24} className="text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground mb-1">
              {isTracking ? formatTime(workingTime)?.split(':')?.slice(0, 2)?.join(':') : '0:00'}
            </p>
            <p className="text-xs text-muted-foreground">Hours Worked</p>
          </div>
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <Icon name="Coffee" size={24} className="text-warning mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground mb-1">
              {Math.floor(totalBreakTime / 60)}
            </p>
            <p className="text-xs text-muted-foreground">Break Minutes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeTracker;